import { GRAPH_NODE_TYPE_REGISTRY, type NodeLabel } from '@atbash-labs/ontology-core'
import { strayPredicateForLabel } from './stray'

export function plantLabelEntitiesCypher(label: NodeLabel | string): string {
  if (!Object.hasOwn(GRAPH_NODE_TYPE_REGISTRY, label)) {
    throw new Error(`Unsupported ontology label: ${label}`)
  }
  const registry = GRAPH_NODE_TYPE_REGISTRY[label as NodeLabel]
  const searchClause = registry.searchableProperties
    .map(
      (property) => `toLower(toString(coalesce(n.\`${property}\`, ''))) CONTAINS toLower($search)`
    )
    .join('\n      OR ')

  const stray = strayPredicateForLabel('n', label as NodeLabel)
  const strayClause =
    stray === null
      ? ''
      : `
      AND (
        $stray = 'all'
        OR (
          ${stray}
        )
      )`

  const whereClause = `
      n.id IS NOT NULL
      AND ($search IS NULL OR ${searchClause})
      AND (
        $validity = 'all'
        OR ($validity = 'invalidated' AND n.valid = false)
        OR ($validity = 'trusted' AND coalesce(n.valid, true) = true)
      )
      AND ($minConfidence IS NULL OR n.confidence >= $minConfidence)${strayClause}`

  // When the label is stray-exempt, skip the isStray projection (always []).
  // Otherwise compute isStray for the page so the Model browser can highlight
  // strays even when the filter is "all".
  const pageCall =
    stray === null
      ? `
    CALL {
      MATCH (n:\`${label}\`)
      WHERE ${whereClause}
      WITH n
      ORDER BY toLower(toString(coalesce(n.\`${registry.displayLabelField}\`, n.id))), n.id
      SKIP toInteger($skip)
      LIMIT toInteger($pageSize)
      RETURN collect(n) AS entities, [] AS strayIds
    }`
      : `
    CALL {
      MATCH (n:\`${label}\`)
      WHERE ${whereClause}
      WITH n, (
          ${stray}
        ) AS isStray
      ORDER BY toLower(toString(coalesce(n.\`${registry.displayLabelField}\`, n.id))), n.id
      SKIP toInteger($skip)
      LIMIT toInteger($pageSize)
      WITH collect({ n: n, isStray: isStray }) AS rows
      RETURN [row IN rows | row.n] AS entities,
             [row IN rows WHERE row.isStray | row.n.id] AS strayIds
    }`

  return `
    CALL {
      MATCH (n:\`${label}\`)
      WHERE ${whereClause}
      RETURN count(n) AS total
    }
    ${pageCall}
    RETURN total, entities, strayIds
  `
}
